import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ShoppingBagIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  LightBulbIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

// Import our revolutionary business components
import BusinessIntelligenceHub from '../../components/business/BusinessIntelligenceHub';
import ProfitAnalyzer from '../../components/business/ProfitAnalyzer';
import InventoryOptimizer from '../../components/business/InventoryOptimizer';
import CashFlowPredictor from '../../components/business/CashFlowPredictor';

/**
 * Admin Dashboard
 */
const EnhancedDashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalCustomers: 0,
    totalProducts: 0,
    revenueChange: 0,
    ordersChange: 0,
    customersChange: 0,
    productsChange: 0
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWidget, setActiveWidget] = useState('overview'); // 'overview', 'intelligence', 'profit', 'inventory', 'cashflow'
  const [customizationMode, setCustomizationMode] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch enhanced dashboard data with AI insights - ONLY REAL DATA
      const [
        statsRes,
        ordersRes,
        customersRes,
        lowStockRes
      ] = await Promise.all([
        axios.get('/api/admin/dashboard/enhanced-stats'),
        axios.get('/api/admin/orders/recent'),
        axios.get('/api/admin/customers/recent'),
        axios.get('/api/admin/products/low-stock')
      ]);

      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.orders || []);
      setRecentCustomers(customersRes.data.customers || []);
      setLowStockProducts(lowStockRes.data.products || []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data || error.message);
      console.error('Error status:', error.response?.status);
      
      // Show error - don't use mock data
      setStats({
        totalRevenue: 0,
        totalOrders: 0,
        totalCustomers: 0,
        totalProducts: 0,
        revenueChange: 0,
        ordersChange: 0,
        customersChange: 0,
        productsChange: 0,
        profitMargin: 0,
        inventoryTurnover: 0,
        cashFlow: 0,
        customerSatisfaction: 0,
        roi: 0,
        error: true,
        errorMessage: error.response?.data?.message || error.message || 'שגיאה בטעינת הנתונים'
      });
      setRecentOrders([]);
      setRecentCustomers([]);
      setLowStockProducts([]);
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (Number.isNaN(num)) return '₪0';
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'תאריך לא זמין';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'תאריך לא תקין';
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'תאריך לא זמין';
    }
  };

  const getHealthScore = () => {
    const revChange = Number(stats.revenueChange) || 0;
    const profitMargin = Number(stats.profitMargin) || 0;
    const satisfaction = Number(stats.customerSatisfaction) || 0;
    const roi = Number(stats.roi) || 0;
    const scores = [revChange, profitMargin, satisfaction * 20, roi];
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    if (Number.isNaN(avgScore) || avgScore === -Infinity || avgScore === Infinity) {
      return { label: 'נדרש שיפור', color: 'text-red-600', bg: 'bg-red-100' };
    }
    
    if (avgScore >= 25) return { label: 'מעולה', color: 'text-green-600', bg: 'bg-green-100' };
    if (avgScore >= 15) return { label: 'טוב', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (avgScore >= 5) return { label: 'בינוני', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { label: 'נדרש שיפור', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const EnhancedStatCard = ({ title, value, change, trend, icon: Icon, color, additionalInfo, href }) => {
    const changeNum = Number(change) || 0;
    const trendMax = trend?.length ? Math.max(...trend, 1) : 1;
    return (
    <div className="bg-white p-5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm text-gray-500">{title}</p>
        {Icon && (
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        )}
      </div>
      
      <p className="text-2xl font-bold text-gray-900 mb-2">{value}</p>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {changeNum >= 0 ? (
            <ArrowUpIcon className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <ArrowDownIcon className="h-3.5 w-3.5 text-red-600" />
          )}
          <span className={`text-sm font-medium ${changeNum >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {Math.abs(changeNum)}%
          </span>
          <span className="text-xs text-gray-400 mr-1">השבוע</span>
        </div>
        
        {additionalInfo && (
          <div className="text-xs text-gray-400">
            {additionalInfo}
          </div>
        )}
      </div>
      
      {/* Mini trend sparkline */}
      {trend && trend.length > 0 && trendMax > 0 && (
        <div className="mt-3">
          <svg viewBox="0 0 100 20" className="w-full h-4">
            <polyline
              fill="none"
              stroke={changeNum >= 0 ? '#059669' : '#dc2626'}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={trend.map((value, index) => `${(index / (trend.length - 1)) * 100},${20 - ((Number(value) || 0) / trendMax) * 15}`).join(' ')}
            />
          </svg>
        </div>
      )}
      
      {href && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <Link
            to={href}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            צפה בפרטים ←
          </Link>
        </div>
      )}
    </div>
  );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">טוען נתונים...</p>
        </div>
      </div>
    );
  }

  const healthScore = getHealthScore();

  return (
    <div className="space-y-8">
      {/* Dashboard Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">סקירה כללית</h1>
            <div className="flex items-center gap-4 mt-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${healthScore.bg} ${healthScore.color}`}>
                בריאות עסקית: {healthScore.label}
              </span>
              <span className="text-sm text-gray-400">
                עודכן: {new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">{stats.roi}%</div>
                <div className="text-gray-500">ROI</div>
              </div>
              <div className="w-px h-8 bg-gray-200"></div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">{stats.customerSatisfaction}/5</div>
                <div className="text-gray-500">שביעות רצון</div>
              </div>
            </div>
            <Link
              to="/admin/business-analytics"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <ChartBarIcon className="w-4 h-4 ml-2" />
              ניתוח עסקי
            </Link>
          </div>
        </div>
      </div>

      {/* Widget Navigation */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-1 py-1">
          <div className="flex gap-1">
            {[
              { key: 'overview', label: 'סקירה כללית', Icon: ChartBarIcon },
              { key: 'intelligence', label: 'מודיעין עסקי', Icon: LightBulbIcon },
              { key: 'profit', label: 'ניתוח רווחיות', Icon: CurrencyDollarIcon },
              { key: 'inventory', label: 'מלאי', Icon: SparklesIcon },
              { key: 'cashflow', label: 'תזרים מזומנים', Icon: ArrowUpIcon },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setActiveWidget(key)}
                className={`px-3 py-2 rounded-md flex items-center text-sm transition-colors ${
                  activeWidget === key
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="h-4 w-4 ml-1.5" />
                {label}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCustomizationMode(!customizationMode)}
            className="px-3 py-2 rounded-md text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex items-center"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 ml-1.5" />
            התאמה אישית
          </button>
        </div>
      </div>

      {/* Dynamic Content Based on Active Widget */}
      {activeWidget === 'overview' && (
        <>
          {/* Enhanced KPI Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <EnhancedStatCard
              title="סך הכנסות"
              value={formatCurrency(stats.totalRevenue)}
              change={stats.revenueChange}
              trend={stats.revenueTrend || []}
              icon={CurrencyDollarIcon}
              color="bg-green-500"
              additionalInfo={stats.profitMargin != null ? `מרווח: ${stats.profitMargin}%` : undefined}
              href="/admin/analytics"
            />
            
            <EnhancedStatCard
              title="הזמנות"
              value={Number(stats.totalOrders).toLocaleString('he-IL')}
              change={stats.ordersChange}
              trend={stats.ordersTrend || []}
              icon={ShoppingBagIcon}
              color="bg-blue-500"
              additionalInfo={stats.dailyOrderAvg != null ? `ממוצע: ${stats.dailyOrderAvg}/יום` : undefined}
              href="/admin/orders"
            />
            
            <EnhancedStatCard
              title="לקוחות"
              value={Number(stats.totalCustomers).toLocaleString('he-IL')}
              change={stats.customersChange}
              trend={stats.customersTrend || []}
              icon={UsersIcon}
              color="bg-purple-500"
              additionalInfo={stats.avgLtv != null ? `LTV: ${formatCurrency(stats.avgLtv)}` : undefined}
              href="/admin/customers"
            />
            
            <EnhancedStatCard
              title="מוצרים"
              value={Number(stats.totalProducts).toLocaleString('he-IL')}
              change={stats.productsChange}
              trend={stats.productsTrend || []}
              icon={ChartBarIcon}
              color="bg-orange-500"
              additionalInfo={stats.inventoryTurnover != null ? `מחזור: ${stats.inventoryTurnover}×` : undefined}
              href="/admin/products"
            />
          </div>

          {/* Dashboard Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Orders */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">הזמנות אחרונות</h3>
                  <Link
                    to="/admin/orders"
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    צפה בהכל
                  </Link>
                </div>
              </div>
              <div className="p-4">
                {recentOrders.length > 0 ? (
                  <div className="space-y-2">
                    {recentOrders.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{order.order_number}</p>
                          <p className="text-xs text-gray-500">{order.customer_name}</p>
                          <p className="text-xs text-gray-400">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-900">{formatCurrency(order.total_amount)}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${order.statusColor}`}>
                            {order.statusLabel}
                          </span>
                          <div className="text-xs text-gray-400 mt-0.5">
                            מרווח: {order.profit_margin}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ShoppingBagIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">אין הזמנות אחרונות</p>
                  </div>
                )}
              </div>
            </div>

            {/* Low Stock Alerts */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 ml-2" />
                  <h3 className="text-sm font-semibold text-gray-900">התראות מלאי</h3>
                </div>
              </div>
              <div className="p-4">
                {lowStockProducts.length > 0 ? (
                  <div className="space-y-2">
                    {lowStockProducts.slice(0, 5).map((product) => (
                      <div key={product.id} className="flex items-center justify-between p-3 rounded-lg border border-amber-100 bg-amber-50/50">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{product.name}</p>
                          <p className="text-xs text-amber-600">נותרו {product.stock_quantity} יחידות</p>
                          <p className="text-xs text-gray-400">SKU: {product.sku}</p>
                          <p className="text-xs text-red-500">יגמר תוך {product.days_until_stockout} ימים</p>
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-medium text-red-600">
                            סיכון: {formatCurrency(product.revenue_at_risk)}
                          </div>
                          <button className="text-xs bg-gray-900 text-white px-3 py-1 rounded mt-2 hover:bg-gray-800 transition-colors">
                            הזמן עכשיו
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">כל המוצרים במלאי תקין</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Business Intelligence Components */}
      {activeWidget === 'intelligence' && <BusinessIntelligenceHub />}
      {activeWidget === 'profit' && <ProfitAnalyzer />}
      {activeWidget === 'inventory' && <InventoryOptimizer />}
      {activeWidget === 'cashflow' && <CashFlowPredictor />}
    </div>
  );
};

export default EnhancedDashboard;
